import { Button } from "primereact/button";
import { Column } from "primereact/column";
import Reorder from "components/fragments/dashboard/components/tab/reorder/Reorder";
import { ReorderType } from "components/fragments/dashboard/components/tab/reorder/DraggableItem";
import { T } from "services/lang/LanguageService";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { handleRowReorderEvt } from "utils/utils";
import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import EditColumn, { ColumnOptions } from "./EditColumn";
import { IUniversalDatasetTableConfig } from "./CellValuesEditor";
import { IUniversalDatasetTableColumn } from "../UniversalDatasetTableRow";
import { CellType } from "./EditBasicCellDialog";

enum RemoveContentType {
  COLUMN = "column",
  ROW = "row",
}

const newColumnTitle = "New Column";

const ensureColumnOptionIds = (
  config: IUniversalDatasetTableConfig
): IUniversalDatasetTableConfig => {
  if (config.columnsOptions.every(col => col.id)) {
    return config;
  }

  const updatedColumnsOptions = config.columnsOptions.map(col => ({
    ...col,
    id: col.id || uuidv4(),
  }));

  return {
    ...config,
    columnsOptions: updatedColumnsOptions,
  };
};

interface RowData {
  columns: IUniversalDatasetTableColumn[];
  rowIndex: number;
  isSeparator?: boolean;
}

interface CellTypesEditorProps {
  currentConfig: IUniversalDatasetTableConfig;
  setCurrentConfig: (config: IUniversalDatasetTableConfig) => void;
}

const CellTypesEditor = ({ currentConfig, setCurrentConfig }: CellTypesEditorProps) => {
  const [isEditColumnDialogVisible, setEditColumnDialogVisible] = useState<boolean>(false);
  const [editingColumnIndex, setEditingColumnIndex] = useState<number | null>(null);
  const [isColumnsReorderDialogVisible, setIsColumnsReorderDialogVisible] =
    useState<boolean>(false);

  const openEditColumnDialog = (index: number) => {
    setEditingColumnIndex(index);
    setEditColumnDialogVisible(true);
  };

  const addRow = () => {
    const isFirstRow = currentConfig.rows.length === 0;
    const newRow = {
      columns:
        currentConfig.rows[currentConfig.rows.length - 1]?.columns.map((column) => ({
          cell: { type: column.cell.type },
        })) || [],
    };

    const newConfig = {
      ...currentConfig,
      rows: [...currentConfig.rows, newRow],
    };

    if (isFirstRow) {
      newConfig.columnsOptions = [
        ...currentConfig.columnsOptions,
        {
          id: uuidv4(),
          title: newColumnTitle,
          columnHeaderVisibility: true,
          columnVisibility: true,
        },
      ];

      newConfig.rows = newConfig.rows.map(row => ({
        ...row,
        columns: [...row.columns, { cell: { type: CellType.DATASET } }],
      }));
    }

    setCurrentConfig(newConfig);
  };

  const addColumn = () => {
    const newConfig = {
      ...currentConfig,
      columnsOptions: [
        ...currentConfig.columnsOptions,
        {
          id: uuidv4(),
          title: newColumnTitle,
          columnHeaderVisibility: true,
          columnVisibility: true,
        },
      ],
      rows: currentConfig.rows.map(row => ({
        ...row,
        columns: [...row.columns, { cell: { type: CellType.DATASET } }],
      })),
    };
    setCurrentConfig(newConfig);
  };

  const addSeparator = () => {
    const newConfig = {
      ...currentConfig,
      rows: [
        ...currentConfig.rows,
        {
          columns:
            currentConfig.rows[0]?.columns.map(() => ({
              cell: { type: CellType.SEPARATOR },
            })) || [],
          isSeparator: true,
        },
      ],
    };
    setCurrentConfig(newConfig);
  };

  const removeRow = (rowIndex: number) => {
    confirmDialog({
      message: T("confirm-dialog.are-you-sure-you-want-to-remove-this-row"),
      header: T("confirm-dialog.confirmation"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: T("action.remove"),
      rejectLabel: T("word.no"),
      accept: () => {
        const newConfig = {
          ...currentConfig,
          rows: currentConfig.rows.toSpliced(rowIndex, 1),
          columnsOptions: currentConfig.rows.length === 1 ? [] : currentConfig.columnsOptions,
        };
        setCurrentConfig(newConfig);
      },
    });
  };

  const removeColumn = (colIndex: number) => {
    const newConfig = {
      ...currentConfig,
      columnsOptions: currentConfig.columnsOptions.toSpliced(colIndex, 1),
      rows:
        currentConfig.columnsOptions.length === 1
          ? []
          : currentConfig.rows.map(row => ({
              ...row,
              columns: row.columns.toSpliced(colIndex, 1),
            })),
    };
    setCurrentConfig(newConfig);
  };

  const updateCellType = (rowIndex: number, colIndex: number, type: CellType) => {
    const newConfig = {
      ...currentConfig,
      rows: currentConfig.rows.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...row,
            columns: row.columns.map((column, columnIndex) => {
              if (columnIndex === colIndex) {
                return {
                  ...column,
                  cell: {
                    ...column.cell,
                    type,
                    value: undefined,
                    displayDatasetValueTime: undefined,
                    timePattern: undefined,
                  },
                };
              }
              return column;
            }),
          };
        }
        return row;
      }),
    };
    setCurrentConfig(newConfig);
  };

  const handleUpdateColumnOptions = (newColumnOptions: ColumnOptions) => {
    const newConfig = {
      ...currentConfig,
      columnsOptions: currentConfig.columnsOptions.map((header, index) => {
        if (index === editingColumnIndex) {
          return newColumnOptions;
        }
        return header;
      }),
    };
    setCurrentConfig(newConfig);
  };

  const removeButtonTemplate = (index: number, removeContentType: RemoveContentType) => {
    return (
      <Button
        icon="pi pi-times"
        className="p-button-text p-button-danger"
        tooltip={`${removeContentType === RemoveContentType.COLUMN ? T("action.remove-column") : T("action.remove-row")}`}
        tooltipOptions={{ position: "bottom" }}
        onClick={() =>
          removeContentType === RemoveContentType.COLUMN ? removeColumn(index) : removeRow(index)
        }
      />
    );
  };

  const renderColumnHeader = (columnIndex: number) => {
    return (
      <div className="flex justify-content-between align-items-center">
        <div>{currentConfig.columnsOptions[columnIndex]?.title || newColumnTitle}</div>
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          tooltip={T("action.edit-column")}
          onClick={() => openEditColumnDialog(columnIndex)}
        />
      </div>
    );
  };

  const renderTypesCell = (rowData: RowData, columnIndex: number) => {
    return (
      <div>
        {rowData.isSeparator ? (
          <span className="font-bold" />
        ) : (
          <Dropdown
            value={rowData.columns[columnIndex].cell.type}
            options={Object.values(CellType).filter(type => type !== CellType.SEPARATOR)}
            className="w-full"
            onChange={e => {
              updateCellType(rowData.rowIndex, columnIndex, e.value);
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <ConfirmDialog />
      <div className="flex flex-column gap-2">
        <div className="flex flex-row justify-content-between">
          <div className="flex gap-2">
            <Button
              label={T("action.add-row")}
              icon="pi pi-plus"
              className="component-editor-button icon-green"
              onClick={addRow}
            />
            <Button
              label={T("action.add-column")}
              icon="pi pi-plus"
              className="component-editor-button icon-green"
              onClick={addColumn}
              disabled={currentConfig.rows.length === 0}
            />
            <Button
              label={T("action.add-separator")}
              icon="pi pi-plus"
              className="component-editor-button icon-green"
              onClick={addSeparator}
            />
          </div>
          <Button
            label={T("label.reorder-columns")}
            icon="pi pi-arrow-right-arrow-left"
            className="component-editor-button"
            onClick={() => setIsColumnsReorderDialogVisible(true)}
          />
        </div>

        <DataTable
          value={
            currentConfig.rows.length > 0
              ? currentConfig.rows.map((row, index) => ({ ...row, rowIndex: index }))
              : []
          }
          editMode="cell"
          size="small"
          onRowReorder={event =>
            handleRowReorderEvt(currentConfig.rows, setCurrentConfig, currentConfig, "rows")(event)
          }
          reorderableColumns
          reorderableRows
        >
          <Column rowReorder style={{ width: "50px" }} />
          {currentConfig.rows.length > 0 && currentConfig.rows[0]?.columns?.length > 0
            ? currentConfig.rows[0].columns.map((col, colIdx) => (
                <Column
                  key={uuidv4()}
                  field={`columns[${colIdx}].cell.type`}
                  header={renderColumnHeader(colIdx)}
                  body={rowData => renderTypesCell(rowData, colIdx)}
                />
              ))
            : null}

          {currentConfig.rows.length > 0 && (
            <Column
              body={rowData => removeButtonTemplate(rowData.rowIndex, RemoveContentType.ROW)}
              style={{ width: "100px", textAlign: "center" }}
            />
          )}
        </DataTable>
      </div>
      {isEditColumnDialogVisible && editingColumnIndex != null && (
        <EditColumn
          selectedColumnOptions={currentConfig.columnsOptions[editingColumnIndex]}
          setSelectedColumnOptions={handleUpdateColumnOptions}
          isEditColumnDialogVisible={isEditColumnDialogVisible}
          setEditColumnDialogVisible={setEditColumnDialogVisible}
          removeColumn={() => removeColumn(editingColumnIndex)}
        />
      )}
      {isColumnsReorderDialogVisible && (
        <Reorder
          setReorderDialogVisibility={setIsColumnsReorderDialogVisible}
          universalTableConfig={ensureColumnOptionIds(currentConfig)}
          reorderDialogVisibility={isColumnsReorderDialogVisible}
          reorderType={ReorderType.UNIVERSAL_TABLE_COLUMNS}
          onUniversalTableConfigChange={setCurrentConfig}
        />
      )}
    </div>
  );
};

export default CellTypesEditor;
